<template>
    <div
        :class="[baseClasses]"
        class="pointer-events-auto shadow-md flex flex-row overflow-hidden relative py-4 pl-6 pr-4 rounded-lg top-0 gap-3 items-center max-w-full"
        @mouseleave="resumeTimer"
        @mouseenter="pauseTimer"
    >
        <Icon :type="iconType" class="w-8 max-[400px]:hidden" />
        <div class="flex flex-col gap-2 grow-0 min-w-20">
            <div class="flex flex-col gap-1">
                <div v-if="title" class="text-lg font-semibold text-ellipsis overflow-hidden" v-html="title" />
                <div v-if="text" class="text-xs font-regular text-system-light text-ellipsis overflow-hidden" v-html="text" />
                <div v-if="subText" class="text-xs font-regular text-sub text-ellipsis overflow-hidden" v-html="subText" />
            </div>
        </div>
        <div class="flex flex-row max-[500px]:flex-col-reverse max-[500px]:items-end gap-2 items-center">
            <Button v-if="actionFunction" :label="actionLabel" size="sm" :variant="buttonVariant" @click="$emit('action')" />
            <IconButton v-if="closable" variant="transparent-light" class="border border-transparent-light-25" size="sm" icon="close" @click.stop="$emit('close')" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted } from 'vue'
    import { Button, IconButton } from '../../buttons'
    import { Icon, IconType } from '../../icons'
    import { type SnackbarComponent, SnackbarType } from './types'

    const props = withDefaults(defineProps<SnackbarComponent>(), {
        type: SnackbarType.INFO,
        actionLabel: 'More',
        duration: 0
    })

    const emit = defineEmits(['action', 'close'])

    const baseClasses = computed(() => {
        const backgroundOptions = {
            warning: '[background:_linear-gradient(to_right,_theme(colors.warning.500)_8px,_theme(colors.system.dark)__8px)]',
            error: '[background:_linear-gradient(to_right,_theme(colors.error.500)_8px,_theme(colors.system.dark)__8px)]',
            info: '[background:_linear-gradient(to_right,_theme(colors.blue.500)_8px,_theme(colors.system.dark)__8px)]',
            success: '[background:_linear-gradient(to_right,_theme(colors.success.500)_8px,_theme(colors.system.dark)__8px)]'
        }
        const textColorOptions = {
            warning: 'text-warning-500',
            error: 'text-error-500',
            info: 'text-blue-500',
            success: 'text-success-500'
        }
        return [backgroundOptions[props.type], textColorOptions[props.type]]
    })

    const buttonVariant = computed(() => {
        const variantOptions: Record<string, 'light' | 'error' | 'blue' | 'primary'> = {
            warning: 'light',
            error: 'error',
            info: 'blue',
            success: 'primary'
        }
        return variantOptions[props.type]
    })

    const iconType = computed(() => {
        const iconTypes: Record<string, IconType> = {
            warning: 'alert',
            error: 'alert',
            info: 'information',
            success: 'checkCircle'
        }
        return iconTypes[props.type]
    })

    const timer = ref()
    const start = ref<number>(0)
    const remaining = ref<number>(0)

    onMounted(() => {
        setTimer()
    })

    const setTimer = () => {
        if (props.id && props.duration > 0) {
            start.value = new Date().getTime()
            remaining.value = props.duration
            timer.value = setTimeout(() => {
                emit('close')
            }, props.duration)
        }
    }

    const pauseTimer = () => {
        if (!props.pauseOnHover || !props.id) return
        clearTimeout(timer.value)
        remaining.value -= Date.now() - start.value
    }

    const resumeTimer = () => {
        if (!props.pauseOnHover || !props.id) return
        start.value = Date.now()
        timer.value = setTimeout(() => {
            emit('close')
        }, remaining.value)
    }
</script>
